<template>
  <div class="pagination-container">
    <div class="custom-pagination">
      <div class="pagination-pages">
        <span
            v-for="(item, index) in displayPages"
            :key="index"
            @click="handlePageClick(item)"
            :class="['page-number', { active: item === current, ellipsis: item === '...' }]">
          {{ item }}
        </span>
      </div>
      
      <div class="pagination-line"></div>

      <div class="pageCurrent">
        <el-input 
          v-model="inputPage" 
          placeholder="请输入页码" 
          class="pagination-input"
          @keyup.enter.native="handleInputJump"
          @blur="handleInputJump"
          :min="1"
          :max="totalPages">
        </el-input>
        <button
          type="button"
          class="jump-btn"
          @click="handleInputJump">
          跳转
        </button>
      </div>
      
      <div class="pagination-buttons">
        <button class="pagination-btn prev-btn" @click="handlePrev" :disabled="current <= 1">
          <img src="../assets/images/left.png" alt="">
        </button>
        
        <button class="pagination-btn next-btn" @click="handleNext" :disabled="current >= totalPages">
          <img src="../assets/images/right.png" alt="">
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Pagination',
  props: {
    total: {
      type: Number,
      required: true,
      default: 0
    },
    pageSize: {
      type: Number,
      default: 10
    },
    currentPage: {
      type: Number,
      default: 1
    }
  },
  data() {
    return {
      size: this.pageSize,
      current: this.currentPage,
      inputPage: this.currentPage // 输入框绑定的页码值
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.total / this.size);
    },
    displayPages() {
      const pages = [];

      // 移动设备优化：当总页数超过6页时，显示当前页前后各2页
      if (this.totalPages <= 6) {
        // 如果总页数小于等于6，则全部显示
        for (let i = 1; i <= this.totalPages; i++) {
          pages.push(i);
        }
      } else {
        // 总页数超过6页时的显示逻辑
        let start = Math.max(1, this.current - 2);
        let end = Math.min(this.totalPages, this.current + 2);

        // 如果当前页靠近开始，显示前5页
        if (this.current <= 3) {
          start = 1;
          end = Math.min(5, this.totalPages);
        }

        // 如果当前页靠近结束，显示后5页
        if (this.current >= this.totalPages - 2) {
          start = Math.max(1, this.totalPages - 4);
          end = this.totalPages;
        }

        // 添加第一页（如果不在范围内）
        if (start > 1) {
          pages.push(1);
          if (start > 2) {
            pages.push('...');
          }
        }

        // 添加中间页码
        for (let i = start; i <= end; i++) {
          pages.push(i);
        }

        // 添加最后一页（如果不在范围内）
        if (end < this.totalPages) {
          if (end < this.totalPages - 1) {
            pages.push('...');
          }
          pages.push(this.totalPages);
        }
      }

      return pages;
    }
  },
  methods: {
    handlePageClick(page) {
      // 如果点击的是省略号，不执行任何操作
      if (page === '...') {
        return;
      }

      if (page !== this.current) {
        this.current = page;
        this.inputPage = page; // 同步输入框值
        this.$emit('current-change', page);
        // 添加滚动到顶部功能
        this.scrollToTop();
      }
    },
    handlePrev() {
      if (this.current > 1) {
        this.current--;
        this.inputPage = this.current; // 同步输入框值
        this.$emit('current-change', this.current);
        // 添加滚动到顶部功能
        this.scrollToTop();
      }
    },
    handleNext() {
      if (this.current < this.totalPages) {
        this.current++;
        this.inputPage = this.current; // 同步输入框值
        this.$emit('current-change', this.current);
        // 添加滚动到顶部功能
        this.scrollToTop();
      }
    },
    // 新增：处理输入框跳转
    handleInputJump() {
      let page = parseInt(this.inputPage);
      
      // 验证输入值
      if (isNaN(page) || page < 1) {
        this.inputPage = this.current; // 恢复为当前页
        return;
      }
      
      if (page > this.totalPages) {
        page = this.totalPages;
      }
      
      // 如果输入的页码与当前页不同，执行跳转
      if (page !== this.current) {
        this.current = page;
        this.inputPage = page;
        this.$emit('current-change', page);
        this.scrollToTop();
      } else {
        // 即使相同也要确保输入框值正确
        this.inputPage = page;
      }
    },
    // 新增滚动到顶部方法
    scrollToTop() {
      // 使用平滑滚动到页面顶部
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  },
  watch: {
    pageSize: {
      handler(val) {
        this.size = val;
      },
      immediate: true
    },
    currentPage: {
      handler(val) {
        this.current = val;
        this.inputPage = val; // 同步输入框值
      },
      immediate: true
    }
  }
}
</script>

<style lang="less" scoped>
@import '../assets/styles/variables.less';
@import '../assets/styles/mixins.less';

.pagination-container {
  
  .custom-pagination {
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: relative;
    
    .pagination-pages {
      display: flex;
      align-items: center;
      
      .page-number {
        margin: 0 4px;
        min-width: 32px;
        height: 32px;
        line-height: 32px;
        border-radius: 4px;
        font-size: 24px;
        cursor: pointer;
        color: #606266;
        text-align: center;
        
        &:hover {
          color: #19529F;
        }
        
        &.active {
          background-color: #19529F;
          color: white;
        }
        
        &.ellipsis {
          cursor: default;
          &:hover {
            color: #606266;
          }
        }
      }
    }
    
    .pagination-line {
      flex: 1;
      height: 0;
      border: 1px solid;
      border-image:  linear-gradient(270deg, rgba(25, 82, 159, 1), rgba(226, 110, 118, 0.42)) 1 1;
      margin: 0 20px 0 15px;
    }
    
    .pageCurrent {
      display: inline-flex;
      align-items: stretch;
      margin-right: 24px;
      flex-shrink: 0;
      border: 1px solid #E4E7ED;
      border-radius: 999px;
      background-color: #FFFFFF;
      box-shadow: 0 1px 4px 0 rgba(50, 85, 117, 0.08);
      overflow: hidden;
      height: 46px;
      
      .pagination-input {
        width: 60px;
        
        /deep/ .el-input__inner {
          height: 46px;
          line-height: 46px;
          border: none;
          border-radius: 0;
          font-size: 14px;
          font-weight: 500;
          color: #303133;
          text-align: center;
          background-color: transparent;
          box-shadow: none;
          transition: color 0.3s ease;
          padding: 0 10px;
          
          &:focus {
            box-shadow: none;
            outline: none;
          }
          
          &::placeholder {
            color: #C0C4CC;
            font-weight: 400;
          }
        }
      }
      
      .jump-btn {
        padding: 0 16px;
        border: none;
        border-left: 1px solid rgba(96, 98, 102, 0.2);
        background: transparent;
        color: #19529F;
        font-size: 16px;
        font-weight: 600;
        cursor: pointer;
        transition: color 0.3s ease, background-color 0.3s ease;
        display: flex;
        align-items: center;
        justify-content: center;
        
        &:hover {
          background-color: rgba(25, 82, 159, 0.08);
        }
        
        &:focus {
          outline: none;
        }
      }
    }
    
    .pagination-buttons {
      display: flex;
      align-items: center;
      height: 68px;
      flex-shrink: 0;
      
      &::before {
        content: '';
        position: absolute;
        width: 0;
        height: 0;
        top: 50%;
        margin-top: -1px;
      }
      
      .pagination-btn {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 162px;
        height: 68px;
        border-radius: 39px;
        cursor: pointer;
        border: none;
        outline: none;
        margin: 0;
        transition: all 0.3s ease;
        
        img {
          width: 38px;
          height: 30px;
        }
        
        &.prev-btn {
          background: #FFFFFF;
          box-shadow: 0 2px 17px 0 rgba(50, 85, 117, 0.18);
          
          &:hover:not(:disabled) {
            background: #f5f5f5;
          }
          
          &:disabled {
            cursor: not-allowed;
            opacity: 0.5;
          }
        }
        
        &.next-btn {
          margin-left: 15px;
          background: #19529F;
          box-shadow: 0 2px 17px 2px rgba(60, 92, 167, 0.5);
          color: white;
          
          &:hover:not(:disabled) {
            background: #1a62c0;
          }
          
          &:disabled {
            cursor: not-allowed;
            opacity: 0.5;
          }
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .pagination-container {
    .custom-pagination {
      .pagination-line {
        margin: 0 20px 0 15px;
      }
      
      .pageCurrent {
        margin-right: 16px;
        height: 40px;
        
        .pagination-input {
          width: 70px;
          
          /deep/ .el-input__inner {
            height: 40px;
            line-height: 40px;
            font-size: 14px;
            padding: 0 8px;
            border-radius: 4px;
          }
        }
        
        .jump-btn {
          padding: 0 18px;
          font-size: 16px;
        }
      }
      
      .pagination-buttons {
        height: 50px;
        
        .pagination-btn {
          width: 120px;
          height: 50px;
          border-radius: 25px;
          
          img {
            width: 24px;
            height: 20px;
          }
        }
      }
    }
  }
}
</style>